import { Typography, useTheme, Box, Tooltip } from '@mui/material';
import {
  type GridRowData,
  type GridColumns,
  type GridValidRowModel,
} from '@mui/x-data-grid';
import { CustomDataGrid } from 'components';
import { colorTheme } from 'theme';

import { useEffect, useState } from 'react';
import { getContributionHoldingById } from 'service';
import { holdingConstants } from 'constants/constantText';
import { strings } from 'i18n/Strings';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { CurrencyFormater } from 'components/helpers/commonFunctions/CurrencyFormater';
import { UnitFormater } from 'utils/GeneralUtil';

export interface HoldingsListInterface {
  contractId: number;
  fundId: number;
  fundName: string;
  totalUnitsAllocated: string;
  currentPrice: string;
}

const HoldingHistory = (): JSX.Element => {
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);
  const [fundData, setHoldingsData] = useState<HoldingsListInterface[]>([]);
  const { id } = useParams();
  const [pageCount, setPageCount] = useState(null);
  const pageInfo = useSelector((state) => state?.pagination?.holdings);
  useEffect(() => {
    getContributionHoldingById(
      Number(id),
      Number(pageInfo?.page) + 1,
      pageInfo?.pageSize,
    )
      .then((res) => {
        const { basicInfo } = res as { basicInfo: HoldingsListInterface };

        setHoldingsData(basicInfo);
        setPageCount(basicInfo?.length);
      })
      .catch((err) => {
        console.log('error view holding', err);
      });
  }, []);

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: holdingConstants.fundId,
      flex: 0.5,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.fund}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.fundId}
        </Typography>
      ),
    },
    {
      field: holdingConstants.fundName,
      flex: 2,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.fundName}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Tooltip title={row?.description} placement="top">
          <Typography
            variant="h5"
            color={colors.gray[100]}
            fontSize={14}
            sx={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            }}
          >
            {row?.shortDescription}
          </Typography>
        </Tooltip>
      ),
    },

    {
      field: 'Symbol',
      flex: 0.7,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.symbol}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.symbol}
        </Typography>
      ),
    },
    {
      field: 'contractId',
      flex: 0.7,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.contractId}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.contractId}
        </Typography>
      ),
    },
    {
      field: holdingConstants.closePrice,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.closePrice}
        </Typography>
      ),
      flex: 1,
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {CurrencyFormater(Number(row?.currentPrice).toFixed(2))}
        </Typography>
      ),
    },
    {
      field: holdingConstants.totalUnitsAllocated,
      flex: 1,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.totalAllocatedUnits}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {UnitFormater(Number(row?.totalUnitsAllocated))}
        </Typography>
      ),
    },
    {
      field: holdingConstants.totalValue,
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.fundDetailsUnitsValueColumn}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {CurrencyFormater(
            Number(row?.currentPrice) * Number(row?.totalUnitsAllocated),
          )}
        </Typography>
      ),
    },
  ];
  const styles = {
    paperContainer: {
      width: '100%',
      padding: '10px',
      marginBottom: '20px',
    },
    emptyTransactionContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '70vh',
      width: '100%',
    },
    emptyTransactionText: {
      fontFamily: 'Inter!important',
      color: '#838383',
      fontWeight: 500,
      fontSize: '1.125rem',
      lineHeight: 1.2,
    },
  };
  return (
    <>
      {fundData.length > 0 ? (
        <CustomDataGrid
          columns={columns}
          rows={fundData as GridRowData[]}
          getRowId={(row: GridRowData) => `${String(row.fundName)}`}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
          }
          currentPage="holdings"
          pageCount={pageCount}
        />
      ) : (
        <Box sx={styles.emptyTransactionContainer}>
          <Typography sx={styles.emptyTransactionText}>
            {strings.displayText.NotHoldingsThere}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default HoldingHistory;
